import axios from 'axios';
import {
  BASE_API_URL, endpoints, ACCESS_TOKEN_AGE, REFRESH_TOKEN_AGE
} from '../constants';
import {
  getCookie, setCookie, decodeJwt, handleLogoutRedirect
} from './general';

const axiosInstance = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true
});

// Tracks whether a refresh is in flight and queues requests that arrive meanwhile
let isRefreshing = false;
let refreshQueue = [];

function resolveQueue(newToken) {
  refreshQueue.forEach(({ resolve }) => resolve(newToken));
  refreshQueue = [];
}

function rejectQueue(err) {
  refreshQueue.forEach(({ reject }) => reject(err));
  refreshQueue = [];
}

async function attemptTokenRefresh() {
  const refreshToken = getCookie('_rtk');
  if (!refreshToken) {
    handleLogoutRedirect();
    throw new Error('No refresh token');
  }

  const response = await axios.post(
    `${BASE_API_URL}${endpoints.accounts.refresh}`,
    { refreshToken },
    { withCredentials: true }
  );

  const { loginToken, refreshToken: newRefreshToken } = response.data;

  if (!loginToken) {
    handleLogoutRedirect();
    throw new Error('Refresh response missing token');
  }

  setCookie('_tk', loginToken, ACCESS_TOKEN_AGE);

  if (newRefreshToken) {
    setCookie('_rtk', newRefreshToken, REFRESH_TOKEN_AGE);
    const rtkExp = Math.floor(Date.now() / 1000) + REFRESH_TOKEN_AGE;
    setCookie('_rtk_exp', String(rtkExp), REFRESH_TOKEN_AGE);
  }

  return loginToken;
}

axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip auth header for the refresh call itself to avoid recursion
    if (config.url?.includes(endpoints.accounts.refresh)) {
      return config;
    }

    let token = getCookie('_tk');
    const decoded = token ? decodeJwt(token) : null;
    const now = Math.floor(Date.now() / 1000);

    // Proactively refresh if token is missing or expired
    if (!token || !decoded?.exp || decoded.exp <= now) {
      if (isRefreshing) {
        // Queue this request until the ongoing refresh completes
        token = await new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        });
      } else {
        isRefreshing = true;
        try {
          token = await attemptTokenRefresh();
          resolveQueue(token);
        } catch (err) {
          rejectQueue(err);
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }
    }

    const cfg = { ...config };
    cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status === 401 && !originalRequest.retried) {
      originalRequest.retried = true;

      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        } catch (queueErr) {
          return Promise.reject(queueErr);
        }
      }

      isRefreshing = true;
      try {
        const newToken = await attemptTokenRefresh();
        resolveQueue(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshErr) {
        rejectQueue(refreshErr);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
